// ── Legal document content ─────────────────────────────────────────────────
// Shared by the marketing footer (Landing/Footer.jsx) and the standalone
// legal pages (pages/legal/LegalDocPage.jsx) so there is exactly one copy
// of this text, reachable both from an in-page link and a real, crawlable
// URL (required for the Google Play "Privacy policy URL" field and the
// account-deletion disclosure — a modal alone cannot satisfy either).
//
// Every [REQUIRED: ...] marker is a fact only Mikkshebs System Technology
// S.R.L. can supply (registration numbers, addresses, licensing status,
// retention periods, fee structure). Nothing here invents those facts —
// see docs/legal/*.md for the fuller draft this content is kept consistent
// with.
export const LEGAL_DOCS = {
  privacy: {
    label: "Privacy",
    title: "Privacy Policy",
    updated: "1 July 2025",
    sections: [
      {
        heading: "Who we are",
        body: "MoneyMeet is operated by Mikkshebs System Technology S.R.L. [REQUIRED: Romanian company registration number and registered address], together with [REQUIRED: confirm Nigerian entity name and registration number] for operations in Nigeria. MoneyMeet is a cross-border payments platform for consumers and businesses. [REQUIRED: confirm current licensing/regulatory status before publishing — MoneyMeet does not currently claim to be a bank, e-money institution, or licensed payment institution in any jurisdiction unless and until that status is confirmed here.]",
      },
      {
        heading: "Data we collect",
        body: "We collect identity information (name, date of birth, ID documents), contact information (email, phone, address), transaction data (amounts, corridors, recipients), device and usage data (IP address, browser type, session logs), and financial information (funding source details) as required for payment processing and AML compliance.",
      },
      {
        heading: "Why we process your data",
        body: "Your data is processed to provide the payment service, comply with legal obligations (AML/CFT, KYC), detect and prevent fraud, improve the platform, and communicate product updates. We rely on contract performance, legal obligation, legitimate interest, and—where required—explicit consent as our lawful bases.",
      },
      {
        heading: "Who we share it with",
        body: "We share data with regulated identity verification providers, banking and payment partners, fraud detection services, and regulators when required by law. We never sell personal data to third parties for marketing purposes.",
      },
      {
        heading: "Your rights",
        body: "You have the right to access, rectify, erase, restrict, and port your personal data, and to object to processing based on legitimate interest. You can request erasure directly from Settings → Delete Account, or by contacting privacy@moneymeetpay.com. [REQUIRED: confirm the correct data-protection authority to reference for our actual jurisdiction(s) — e.g. Romania's ANSPDCP — rather than the UK's ICO].",
      },
      {
        heading: "Retention",
        body: "Transaction and identity records are retained for up to 7 years after the end of your relationship with us for AML/CFT purposes [REQUIRED: confirm exact retention period and the specific regulation it is required under in each jurisdiction we operate in]. Other data is retained no longer than necessary for the purpose it was collected.",
      },
    ],
  },
  terms: {
    label: "Terms",
    title: "Terms of Service",
    updated: "1 July 2025",
    sections: [
      {
        heading: "Acceptance",
        body: "By creating a MoneyMeet account you agree to these Terms. If you do not agree, you may not use the service. These Terms constitute a binding legal agreement between you and Mikkshebs System Technology S.R.L. [REQUIRED: confirm contracting entity per jurisdiction].",
      },
      {
        heading: "The service",
        body: "MoneyMeet provides a platform to send money internationally. We are not a bank. [REQUIRED: confirm whether any deposit-protection or fund-safeguarding scheme applies to funds held with us, and under what specific regulation — do not assume UK schemes apply, as they do not for this entity.]",
      },
      {
        heading: "Eligibility",
        body: "You must be 18 or older, a resident of a supported country, and able to pass our identity and AML checks to use MoneyMeet. Accounts may not be used for business purposes unless registered as a business account.",
      },
      {
        heading: "Fees",
        body: "[REQUIRED: confirm and state our actual fee structure here before publishing — no fee-calculation logic matching a flat percentage figure exists in the current implementation, so a specific figure must not be asserted as a contractual term until it is verified or the correct figure is supplied.] Receiving banks may apply their own charges, which are outside our control.",
      },
      {
        heading: "Prohibited use",
        body: "You may not use the service to fund illegal activity, evade sanctions, launder money, or circumvent AML controls. Accounts found in violation will be suspended and reported to relevant authorities.",
      },
      {
        heading: "Limitation of liability",
        body: "To the maximum extent permitted by law, MoneyMeet's total liability to you in any 12-month period shall not exceed the fees paid by you in that period. We are not liable for indirect or consequential losses.",
      },
    ],
  },
  refund: {
    label: "Refund",
    title: "Refund / Dispute Policy",
    updated: "1 July 2025",
    sections: [
      {
        heading: "When a transfer fails",
        body: "If a payout provider reports that a transfer has failed or been cancelled before funds reach the recipient, MoneyMeet automatically reverses the transaction and returns the funds to your wallet — this is a real, automatic mechanism (not a manual, discretionary process), triggered directly by the provider's own status webhook.",
      },
      {
        heading: "What we don't yet support",
        body: "Self-service cancellation of a transfer that is already in flight is not currently available — once submitted, a transfer proceeds through the provider unless the provider itself reports a failure. If you need to dispute a transfer for another reason (wrong recipient details, unauthorised transaction, etc.), contact support at support@moneymeetpay.com.",
      },
      {
        heading: "Disputed or unauthorised transactions",
        body: "If you believe a transaction was unauthorised or fraudulent, contact us immediately. [REQUIRED: confirm our target investigation/response timeline and any applicable chargeback-style rights under the regulations we are actually subject to.]",
      },
      {
        heading: "Fees on reversed transfers",
        body: "[REQUIRED: confirm whether any fee is retained on an automatically-reversed transfer, or whether the full amount including fees is returned.]",
      },
    ],
  },
  cookies: {
    label: "Cookies",
    title: "Cookie Policy",
    updated: "1 July 2025",
    sections: [
      {
        heading: "What are cookies",
        body: "Cookies are small text files placed on your device when you visit our website or use the web app. They help us remember your preferences, keep you logged in, and understand how you use the platform.",
      },
      {
        heading: "Strictly necessary cookies",
        body: "These cookies are essential for the platform to function. They include authentication session tokens, CSRF protection tokens, and security identifiers. You cannot opt out of these cookies.",
      },
      {
        heading: "Analytics cookies",
        body: "[REQUIRED: confirm which, if any, analytics tooling is actually deployed on the production website before this section is published — describe only what is actually running.]",
      },
      {
        heading: "Preference cookies",
        body: "These cookies remember your settings, such as your chosen display theme and preferred language. They expire after 12 months.",
      },
      {
        heading: "Managing cookies",
        body: "You can control cookies through your browser settings. Blocking strictly necessary cookies will prevent the platform from working correctly. Blocking other cookies will not affect core functionality.",
      },
    ],
  },
  compliance: {
    label: "Compliance",
    title: "Compliance",
    updated: "1 July 2025",
    sections: [
      {
        heading: "Regulatory status",
        body: "[REQUIRED: confirm and state MoneyMeet's/Mikkshebs System Technology S.R.L.'s actual licensing and regulatory status in each jurisdiction it operates in. MoneyMeet does not claim to be an Electronic Money Institution, bank, or licensed payment institution unless and until that status is confirmed here — this section must not be published with an unverified claim.]",
      },
      {
        heading: "Anti-money laundering",
        body: "We verify the identity of our customers (Know Your Customer) and monitor transactions for suspicious activity (AML). [REQUIRED: confirm which regulator/financial-intelligence unit suspicious activity is reported to in our actual operating jurisdiction(s).]",
      },
      {
        heading: "Sanctions screening",
        body: "Customers and transactions are screened against sanctions lists (currently OFAC) before and during the customer relationship. Transactions involving sanctioned individuals, entities, or jurisdictions are blocked and reported. [REQUIRED: confirm whether additional sanctions lists (EU, UN, or others relevant to our operating jurisdictions) should also be represented here.]",
      },
      {
        heading: "Safeguarding",
        body: "[REQUIRED: confirm and describe how customer funds are actually safeguarded before publishing this section — do not reference UK Electronic Money Regulations safeguarding requirements unless MoneyMeet/Mikkshebs System Technology S.R.L. is in fact subject to them.]",
      },
      {
        heading: "Complaints",
        body: "To make a complaint, contact complaints@moneymeetpay.com. We will respond within a reasonable time. [REQUIRED: confirm target response time and the correct external complaints body, if any, for our actual jurisdiction(s) — do not reference the UK Financial Ombudsman Service unless applicable.]",
      },
    ],
  },
  "delete-account": {
    label: "Delete Account",
    title: "Account & Data Deletion",
    updated: "1 July 2025",
    sections: [
      {
        heading: "How to delete your account",
        body: "Signed-in users can delete their account directly from the app or website: Settings → Delete Account. You will be asked to re-enter your password and type DELETE to confirm — this is a permanent, immediate action, not a request that waits on staff review.",
      },
      {
        heading: "What happens to your data",
        body: "Your name, date of birth, contact details, and identity documents are anonymised immediately. Your login is deactivated immediately and cannot be used again. Financial transaction records are not deleted at the same time — see the next section for why.",
      },
      {
        heading: "Why some records aren't deleted immediately",
        body: "Anti-money-laundering rules require keeping certain transaction records for a minimum period after your last transaction. [REQUIRED: confirm applicable legal/data-retention requirements — the current default configured in the system is 7 years, but this is a technical default, not a confirmed legal citation, and must be verified against the actual regulations MoneyMeet is subject to in each jurisdiction before this page asserts it as fact.] If you have no transactions inside that window, deletion completes immediately with no delay.",
      },
      {
        heading: "If you can't sign in",
        body: "If you're unable to sign in to use the in-app deletion flow, contact privacy@moneymeetpay.com from the email address on your account and we will process your request manually, subject to identity verification.",
      },
    ],
  },
};
